package uz.ferro.erp.manager

import kotlinx.browser.localStorage
import kotlinx.serialization.encodeToString
import org.w3c.dom.get
import org.w3c.dom.set
import uz.ferro.erp.KEY_AUTH_TOKEN
import uz.ferro.erp.KEY_ROLE
import uz.ferro.erp.KEY_USER
import uz.ferro.erp.api.jsonSerializer
import uz.ferro.erp.api.suspendPost
import uz.ferro.erp.error.HttpException
import uz.ferro.erp.feature.auth.model.EmailAuthBody
import uz.ferro.erp.feature.auth.model.EmailAuthConfirmBody
import uz.ferro.erp.feature.auth.model.EmailPreConfirmationResult
import uz.ferro.erp.feature.auth.model.RegisterBody
import uz.ferro.erp.feature.user.model.Role
import uz.ferro.erp.feature.user.model.User
import uz.ferro.erp.model.AuthBody
import uz.ferro.erp.model.AuthResult

object AuthManager {

    var role: Role?
        get() = Role.entries.firstOrNull { it.name == localStorage[KEY_ROLE] }
        set(value) {
            localStorage[KEY_ROLE] = value?.name.orEmpty()
        }

    var token: String?
        get() = localStorage[KEY_AUTH_TOKEN]
        set(value) {
            localStorage[KEY_AUTH_TOKEN] = value.orEmpty()
        }

    var user: User?
        get() = localStorage[KEY_USER]?.let {
            try {
                jsonSerializer.decodeFromString(it)
            } catch (e: Exception) {
                null
            }
        }
        set(value) {
            localStorage[KEY_USER] = jsonSerializer.encodeToString(value)
        }

    fun isAuthorized(): Boolean = token.orEmpty().isNotBlank()

    fun isAdmin(): Boolean = isAuthorized() && role?.isAdmin() == true

    fun isSuperAdmin(): Boolean = isAuthorized() && role?.isSuperAdmin() == true

    fun canUseHomeDashboard(): Boolean = isAdmin() || isSuperAdmin()

    fun logout() {
        role = null
        token = null
        user = null
    }

    suspend fun checkAuthorization() {
        if (isAuthorized()) {
            try {
                UserManager.getMe()
            } catch (e: Exception) {
                if (e is HttpException) {
                    if (e.code == 401) {
                        logout()
                    }
                }
            }
        }
    }

    private suspend fun saveAuthData(result: AuthResult) {
        if (result.token.orEmpty().isNotBlank()) {
            token = result.token
            role = Role.entries.firstOrNull { it.name == result.user?.role?.name }
            user = result.user
        }
    }

    suspend fun authWithGoogle(jwt: String): AuthResult {
        val result = suspendPost<AuthBody, AuthResult>(
            path = "auth/google",
            AuthBody(
                externalToken = jwt
            )
        )
        saveAuthData(result)
        return result
    }

    suspend fun authWithEmail(email: String): EmailPreConfirmationResult {
        val result = suspendPost<EmailAuthBody, EmailPreConfirmationResult>(
            path = "auth/email",
            EmailAuthBody(email = email)
        )
        return result
    }

    suspend fun register(registerBody: RegisterBody): EmailPreConfirmationResult {
        val result = suspendPost<RegisterBody, EmailPreConfirmationResult>(
            path = "auth/register",
            body = registerBody
        )
        return result
    }

    suspend fun confirmEmail(email: String, code: String): AuthResult {
        val result = suspendPost<EmailAuthConfirmBody, AuthResult>(
            path = "auth/email/confirm",
            EmailAuthConfirmBody(email = email, code = code)
        )

        saveAuthData(result)

        return result
    }

    suspend fun authenticate(login: String, password: String): AuthResult {
        val result = suspendPost<AuthBody, AuthResult>(
            path = "auth",
            AuthBody(
                username = login,
                password = password
            )
        )

        if (result.token.orEmpty().isNotBlank()) {
            token = result.token
            role = Role.entries.firstOrNull { it.name == result.user?.role?.name }
            user = result.user
        }

        return result
    }
}